import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import { FaTrash } from 'react-icons/fa';
import { feedbackApi } from '../../../services/api';
import {
  DELETE_SESSION_TITLE,
  DELETION_PREVIEW_STALE_CODE,
  getConfirmedCounts,
  getDeletionImpactLines
} from '../../../utils/feedbackSessionDeletion';

const MAX_LISTED_INDIVIDUALS = 8;

/**
 * ADR-193: confirmation for deleting a feedback session, with or without
 * individuals, in any status.
 *
 * On open it loads the deletion preview and shows exactly what will be
 * destroyed (individuals, completed assignments, reviewers, responses,
 * unsubmitted Start/Stop/Continue) plus the names of the individuals. A reason
 * is mandatory (ADR-192 §12). Delete sends the reason and the counts shown here
 * (`confirmedCounts`); if the session changed in the meantime the API answers
 * 409 DELETION_PREVIEW_STALE, and this dialog reloads the preview, keeps the
 * reason and asks the user to confirm the new numbers.
 *
 * `onConfirm(reason, confirmedCounts)` must return a promise that rejects with
 * the ApiError for a stale preview, so the dialog can refresh itself.
 */
const DeleteSessionModal = ({ isOpen, onClose, onConfirm, session }) => {
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staleNotice, setStaleNotice] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!session?.id) {
      return;
    }
    setIsLoadingPreview(true);
    setPreviewError(null);
    try {
      const result = await feedbackApi.getSessionDeletionPreview(session.id);
      setPreview(result);
    } catch (error) {
      setPreview(null);
      setPreviewError(error.message || 'Failed to load what will be deleted');
    } finally {
      setIsLoadingPreview(false);
    }
  }, [session?.id]);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setPreview(null);
      setStaleNotice(false);
      setIsSubmitting(false);
      loadPreview();
    }
  }, [isOpen, loadPreview]);

  const trimmedReason = reason.trim();
  const canConfirm = Boolean(preview) && Boolean(trimmedReason) && !isSubmitting && !isLoadingPreview;

  const handleConfirm = async () => {
    if (!canConfirm) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm(trimmedReason, getConfirmedCounts(preview));
    } catch (error) {
      if (error?.status === 409 && error?.data?.code === DELETION_PREVIEW_STALE_CODE) {
        setStaleNotice(true);
        await loadPreview();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const impactLines = getDeletionImpactLines(preview);
  const individuals = preview?.individuals || [];
  const listedIndividuals = individuals.slice(0, MAX_LISTED_INDIVIDUALS);
  const overflowCount = individuals.length - listedIndividuals.length;
  const hasIndividuals = Boolean(preview?.assignmentCount);

  return (
    <Dialog open={isOpen} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <FaTrash color="#EF4444" />
        {DELETE_SESSION_TITLE}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          You are about to delete the feedback session
          {' '}<strong>&quot;{session?.name}&quot;</strong>.
        </Typography>

        {staleNotice && (
          <Box sx={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE047', borderRadius: '8px', p: 1.5, mb: 2 }}>
            <Typography sx={{ fontSize: '14px' }}>
              The session changed since these numbers were loaded. They have been updated below. Please review them and confirm again.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            p: 2,
            mb: 2
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '14px' }}>
            This action cannot be undone
          </Typography>

          {isLoadingPreview && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '14px' }}>Checking what will be deleted…</Typography>
            </Box>
          )}

          {!isLoadingPreview && previewError && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '14px', color: '#B91C1C' }}>{previewError}</Typography>
              <Button size="small" onClick={loadPreview}>Try again</Button>
            </Box>
          )}

          {!isLoadingPreview && preview && (
            <>
              {hasIndividuals && (
                <Typography sx={{ fontSize: '14px', mb: 0.5 }}>This will permanently delete:</Typography>
              )}
              <Box component={hasIndividuals ? 'ul' : 'div'} sx={{ m: 0, pl: hasIndividuals ? 2.5 : 0, fontSize: '14px' }}>
                {impactLines.map((line) => (
                  hasIndividuals
                    ? <li key={line}><Typography component="span" sx={{ fontSize: '14px' }}>{line}</Typography></li>
                    : <Typography key={line} sx={{ fontSize: '14px' }}>{line}</Typography>
                ))}
              </Box>

              {preview.preservedSubmittedFoDCount > 0 && (
                <Typography sx={{ fontSize: '13px', mt: 1 }}>
                  {preview.preservedSubmittedFoDCount} submitted Start/Stop/Continue
                  {preview.preservedSubmittedFoDCount === 1 ? ' is' : ' are'} kept, because the
                  individual may already have seen it.
                </Typography>
              )}

              {listedIndividuals.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                  {listedIndividuals.map((individual) => (
                    <Chip
                      key={individual.assignmentId}
                      size="small"
                      label={individual.fullName || 'Unknown individual'}
                      sx={{ backgroundColor: '#FFFFFF', border: '1px solid #FECACA', fontSize: '12px' }}
                    />
                  ))}
                  {overflowCount > 0 && (
                    <Chip
                      size="small"
                      label={`and ${overflowCount} more`}
                      sx={{ backgroundColor: '#FFFFFF', border: '1px solid #FECACA', fontSize: '12px', fontStyle: 'italic' }}
                    />
                  )}
                </Box>
              )}

              <Typography sx={{ fontSize: '13px', mt: 1.5, color: '#475569' }}>
                The session&apos;s history stays in the Feedback Modification Audit report, together with the reason you give here.
              </Typography>
            </>
          )}
        </Box>

        <TextField
          label="Reason for deleting (required)"
          placeholder="e.g. Duplicate of the Q3 session, created by mistake"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
          autoFocus
          disabled={isSubmitting}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
          sx={{
            backgroundColor: '#EF4444',
            '&:hover': { backgroundColor: '#DC2626' }
          }}
        >
          {isSubmitting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteSessionModal;
